import React, { useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/firebase/firestore/use-collection';
import type { Jarticard, MarketListing, MarketOffer } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { makeMarketOffer, acceptMarketOffer, rejectMarketOffer, cancelMarketListing } from '@/lib/actions';
import { JarticardFrame } from './JarticardFrame';
import { Repeat, Loader2, X, Check, Store, Inbox } from 'lucide-react';

export function TradeMarket({ cardsById, ownedMap }: { cardsById: Map<string, Jarticard>; ownedMap: Map<string, number> }) {
  const { user } = useAuth();

  const listingsQuery = useMemo(() => query(collection(db, 'jarticard_market'), where('status', '==', 'open')), []);
  const { data: rawListings } = useCollection<MarketListing>(listingsQuery);

  const listings = useMemo(
    () => (rawListings || []).slice().sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))),
    [rawListings],
  );

  const myListings = listings.filter((l) => l.sellerId === user?.uid);
  const otherListings = listings.filter((l) => l.sellerId !== user?.uid);

  // Mis cartas repetidas (count >= 2) para ofertar
  const myRepes = useMemo(() => {
    const out: Jarticard[] = [];
    ownedMap.forEach((count, cardId) => {
      if (count >= 2) {
        const c = cardsById.get(cardId);
        if (c) out.push(c);
      }
    });
    return out.sort((a, b) => a.number - b.number);
  }, [ownedMap, cardsById]);

  return (
    <div className="space-y-10">
      {/* Tus publicaciones */}
      <section className="space-y-4">
        <SectionHeader icon={Inbox} title="Tus publicaciones" subtitle="Las cartas repetidas que ofreces a la comunidad" color="#f59e0b" count={myListings.length} />
        {myListings.length === 0 ? (
          <EmptyState icon={Inbox} text="No tienes cartas publicadas. Abre tu álbum y publica una carta repetida para recibir ofertas." />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {myListings.map((l) => (
              <MyListingCard key={l.id} listing={l} cardsById={cardsById} />
            ))}
          </div>
        )}
      </section>

      {/* Explorar mercado */}
      <section className="space-y-4">
        <SectionHeader icon={Store} title="Explorar mercado" subtitle="Ofrece una repe tuya por la carta que te falta" color="#38bdf8" count={otherListings.length} />
        {otherListings.length === 0 ? (
          <EmptyState icon={Store} text="No hay cartas de otros usuarios en el mercado ahora mismo." />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {otherListings.map((l) => (
              <BrowseListingCard key={l.id} listing={l} cardsById={cardsById} myRepes={myRepes} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color, count }: { icon: React.ElementType; title: string; subtitle: string; color: string; count: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-11 w-11 place-items-center rounded-xl border" style={{ color, borderColor: `${color}55`, background: `${color}1f` }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <h2 className="font-headline text-xl font-bold leading-tight">{title}</h2>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <span className="rounded-full bg-white/8 px-3 py-1 text-sm font-bold">{count}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="grid place-items-center gap-3 rounded-2xl border border-dashed border-white/10 bg-card/20 py-14 text-center">
      <Icon className="h-9 w-9 text-muted-foreground/40" />
      <p className="max-w-md text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

// ── Tarjeta de publicación propia (con ofertas recibidas) ──

function MyListingCard({ listing, cardsById }: { listing: MarketListing; cardsById: Map<string, Jarticard> }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const card = cardsById.get(listing.cardId);

  const offersQuery = useMemo(
    () => collection(db, 'jarticard_market', listing.id, 'offers'),
    [listing.id],
  );
  const { data: rawOffers } = useCollection<MarketOffer>(offersQuery);
  const pending = (rawOffers || []).filter((o) => o.status === 'pending');

  const handleAccept = async (offerId: string) => {
    setBusy(offerId);
    const res = await acceptMarketOffer(listing.id, offerId);
    setBusy(null);
    toast({ title: res.success ? '¡Intercambio hecho!' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    if (res.success) setOpen(false);
  };
  const handleReject = async (offerId: string) => {
    setBusy(offerId);
    const res = await rejectMarketOffer(listing.id, offerId);
    setBusy(null);
    if (!res.success) toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };
  const handleCancel = async () => {
    setBusy('cancel');
    const res = await cancelMarketListing(listing.id);
    setBusy(null);
    toast({ title: res.success ? 'Cancelada' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
  };

  if (!card) return null;

  return (
    <div className="space-y-2">
      <div className="relative">
        <JarticardFrame thumb card={card} owned />
        {pending.length > 0 && (
          <span className="absolute -right-2 -top-2 z-40 grid h-6 min-w-6 place-items-center rounded-full bg-emerald-500 px-1.5 text-xs font-bold text-white shadow-lg">
            {pending.length}
          </span>
        )}
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="secondary" className="flex-1" onClick={() => setOpen(true)}>
          Ofertas ({pending.length})
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} disabled={busy === 'cancel'}>
          {busy === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ofertas por «{card.name}»</DialogTitle>
            <DialogDescription>Te ofrecen estas cartas a cambio. Acepta la que prefieras.</DialogDescription>
          </DialogHeader>
          {pending.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Todavía no hay ofertas.</p>
          ) : (
            <div className="space-y-3">
              {pending.map((offer) => {
                const offered = cardsById.get(offer.offeredCardId);
                return (
                  <div key={offer.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-card/40 p-2">
                    <div className="w-16 shrink-0">{offered && <JarticardFrame thumb card={offered} owned />}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{offered?.name || 'Carta'}</p>
                      <p className="text-xs text-muted-foreground">de {offer.offererName}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => handleReject(offer.id)} disabled={busy === offer.id}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button size="icon" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAccept(offer.id)} disabled={busy === offer.id}>
                      {busy === offer.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Tarjeta de publicación de otro usuario (para ofertar) ──

function BrowseListingCard({ listing, cardsById, myRepes }: { listing: MarketListing; cardsById: Map<string, Jarticard>; myRepes: Jarticard[] }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const card = cardsById.get(listing.cardId);

  const handleOffer = async (offeredCardId: string) => {
    setBusy(true);
    const res = await makeMarketOffer(listing.id, offeredCardId);
    setBusy(false);
    toast({ title: res.success ? 'Oferta enviada' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    if (res.success) setOpen(false);
  };

  if (!card) return null;

  return (
    <div className="space-y-2">
      <JarticardFrame thumb card={card} owned />
      <p className="truncate text-center text-xs text-muted-foreground">de {listing.sellerName}</p>
      <Button size="sm" className="w-full" onClick={() => setOpen(true)}>
        <Repeat className="h-4 w-4" /> Ofrecer cambio
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ofrecer una carta por «{card.name}»</DialogTitle>
            <DialogDescription>Elige una de tus cartas repetidas para ofrecerla a cambio.</DialogDescription>
          </DialogHeader>
          {myRepes.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No tienes cartas repetidas para ofrecer.</p>
          ) : (
            <div className="grid max-h-[60vh] grid-cols-3 gap-3 overflow-y-auto p-1 sm:grid-cols-4 md:grid-cols-5">
              {myRepes.map((c) => (
                <button
                  key={c.id}
                  onClick={() => !busy && handleOffer(c.id)}
                  disabled={busy}
                  className="rounded-xl ring-offset-2 ring-offset-background transition hover:ring-2 hover:ring-amber-400 disabled:opacity-50"
                >
                  <JarticardFrame thumb card={c} owned />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
